import {
  Box,
  SimpleGrid,
  Center,
  Divider,
  HStack,
  useToast,
  Button,
  Text,
  FormControl,
  FormErrorMessage,
  Input,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { useUpdateUserSettingsMutation } from './user.gql.generated';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  getSingleErrorMessageFromFieldErrors,
  InputFormFieldProps,
} from '~/packages/common-frontend/components/FormField';
import { Icon } from '~/packages/common-frontend/components/icons';
import { LabeledDetail } from '~/packages/common-frontend/components/LabeledDetail';
import {
  toastId,
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

const { capitalize } = lodash;

// Fragments
const SettingsFormField = ({
  isRequired,
  handleChange,
  propName,
  formProps,
  children,
  label,
  placeholder,
}: InputFormFieldProps & { children?: React.ReactNode }) => {
  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      {label && (
        <Text fontSize="14px" color="#979797" fontWeight="bold" mb="5px">
          {label}
        </Text>
      )}

      <Input
        placeholder={placeholder}
        name={propName}
        value={formProps.values[propName]}
        onChange={(e) => {
          formProps.handleChange(e);
          if (handleChange) {
            handleChange(e);
          }
        }}
        onBlur={formProps.handleBlur}
      />
      <FormErrorMessage mt="0">
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
      {children}
    </FormControl>
  );
};

// Exports
export const SettingsEditForm = ({
  setEditMode,
}: {
  setEditMode: Function;
}) => {
  const { user } = useUserAuthOrThrow();
  const [userInfo, setUserInfo] = useState({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
  };
  const formValidation = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
  });

  const toast = useToast();
  const navigate = useNavigate();

  const [mutate] = useUpdateUserSettingsMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const { data } = await mutate({
        variables: {
          id: userInfo.id,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
        },
      });

      if (data && data.updateUserSettings && !toast.isActive(toastId)) {
        const response = data.updateUserSettings.response;
        successToastConfig.title = response;
        // don't toast for now, since this navigate(0) will refresh
        // the settings page
        // toast(successToastConfig);
        setIsSubmitting(false);
        navigate(0);
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = 'Error updating user settings.';
        toast(errorToastConfig);
        setIsSubmitting(false);
      }
      throw err;
    }
  }, [mutate, userInfo, navigate, toast]);

  const handleClose = () => {
    setEditMode(false);
  };

  return (
    <Box>
      <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX={10} spacingY="20px">
        <LabeledDetail label="Organization">
          {user.organization.name}
        </LabeledDetail>
        <LabeledDetail label="Industry">
          {user.organization.industry}
        </LabeledDetail>
      </SimpleGrid>

      <Center height="50px">
        <Divider />
      </Center>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={formValidation}
      >
        {(formProps) => (
          <Form data-testid="settings-user-edit-form">
            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacingX={10}
              spacingY="20px"
            >
              <SettingsFormField
                isRequired
                handleChange={handleChange}
                propName="firstName"
                label="First Name"
                placeholder="John"
                inputWidth="80%"
                formProps={formProps}
              />
              <SettingsFormField
                isRequired
                handleChange={handleChange}
                propName="lastName"
                label="Last Name"
                inputWidth="80%"
                placeholder="Doe"
                formProps={formProps}
              />
              <LabeledDetail label="Email">{user.email}</LabeledDetail>
              <LabeledDetail label="Role">
                {capitalize(user.roleType)}
              </LabeledDetail>
            </SimpleGrid>

            <Box height={50} />
            <HStack spacing={5}>
              <Button
                aria-label="Cancel"
                variant="secondary"
                colorScheme="v2-aurora"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                aria-label="Save"
                variant="primary"
                colorScheme="v2-aurora"
                type="submit"
                rightIcon={<Icon icon="check" />}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
